import { valibotResolver } from "@hookform/resolvers/valibot";
import { Check, PencilSimpleLine, UsersThree, X } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { Input } from "common/components/ui/Input";
import { Tag } from "common/components/ui/Tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { formatNumber } from "common/helpers/utils";
import {
  AudienceSizeSchema,
  SearchCampaignAudienceSizeSchema,
} from "common/schemas";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useAudienceMutations from "../../../../datahooks/useAudienceMutations";
import useAudienceFilters from "../../../../hooks/useAudienceFilters";
import SectionHeading from "../../SectionHeading";
import useAudienceProgress from "./useAudienceProgress";

import type { AudienceFilters } from "../../../../types";

interface AudienceHeaderProps {
  isMiningTargets: boolean;
  filters: AudienceFilters;
  setFilters: (filters: AudienceFilters) => void;
}

export default function AudienceHeader({
  isMiningTargets,
  filters,
  setFilters,
}: AudienceHeaderProps) {
  const [isEditingLimit, setIsEditingLimit] = useState(false);
  const { type, audience_size: audienceSize } = useCampaignContext();

  const { updateAudienceSize, isUpdatingAudienceSize } =
    useAudienceMutations(filters);
  const { reconnectSocket } = useAudienceProgress(filters);
  const { resetAllFilters } = useAudienceFilters(filters, setFilters);

  const isList = type === "list" || type === "crm";
  const canEditAudienceSize = type !== "drip" && !isList;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { size: audienceSize },
    resolver: valibotResolver(
      type === "search" ? SearchCampaignAudienceSizeSchema : AudienceSizeSchema,
    ),
  });

  function discardEdit() {
    setIsEditingLimit(false);
    reset();
  }

  function handleEditLimit({ size }: { size: number }) {
    updateAudienceSize({
      size,
    }).then(() => {
      // Reconnect to the socket to get the new audience size
      reconnectSocket();

      // Reset the form & close the edit mode
      reset({ size });
      setIsEditingLimit(false);

      // Reset all filters after updating audience size
      resetAllFilters();
    });
  }

  let editButton = (
    <Button
      intent="iconOnly"
      variant="secondary-black"
      size="sm"
      onClick={() => setIsEditingLimit(true)}
      disabled={isMiningTargets}
    >
      <PencilSimpleLine />
    </Button>
  );

  if (isMiningTargets) {
    editButton = (
      <Tooltip>
        <TooltipTrigger asChild>{editButton}</TooltipTrigger>
        <TooltipContent align="end">
          Cannot edit number of targets while mining is in progress
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <article className="flex items-center gap-4">
      <SectionHeading
        title="Audience preview"
        subtitle="A detailed look at your audience for this campaign"
      />

      <RenderIf condition={canEditAudienceSize}>
        <div
          className={clsx(
            "ml-auto flex items-center gap-2",
            isEditingLimit && "max-md:flex-col",
          )}
        >
          {isEditingLimit ? (
            <>
              <Input
                variant="sm"
                disabled={isUpdatingAudienceSize}
                leftComponent={<UsersThree weight="fill" />}
                className="max-w-24 shrink-0"
                type="number"
                hasError={!!errors.size?.message}
                {...register("size", {
                  valueAsNumber: true,
                })}
              />
              <div className="flex gap-2">
                <Button
                  intent="iconOnly"
                  variant="outline-black"
                  size="sm"
                  onClick={discardEdit}
                  disabled={isUpdatingAudienceSize}
                >
                  <X />
                </Button>
                <Button
                  intent="iconOnly"
                  variant="secondary-purple"
                  size="sm"
                  onClick={handleSubmit(handleEditLimit)}
                  isLoading={isUpdatingAudienceSize}
                >
                  <Check />
                </Button>
              </div>
            </>
          ) : (
            <>
              <Tag
                size="md"
                leftIcon={<UsersThree weight="fill" />}
                variant="quaternary"
              >
                {formatNumber(audienceSize)}
              </Tag>
              {editButton}
            </>
          )}
        </div>
      </RenderIf>
    </article>
  );
}
